/*
 * Copyright (C) 2024 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and
 * should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly
 * prohibited.
 */

import { AutocompleteProps, Box, TextField } from '@mui/material';
import { useField, useFormikContext } from 'formik';
import * as React from 'react';
import { BeamInputLabel, BeamInputLabelProps } from './BeamInputLabel';
import { BeamAutocomplete } from './BeamAutocomplete';
import { BeamHelpText } from './BeamHelpText';

type BeamSelectOnChange = (event: React.ChangeEvent<{}>, value: any, reason: string, details: any) => any;

/**
 * Props for the BeamSelect component.
 */
export interface BeamSelectProps {
  /**
   * The name of the field, used for formik field binding.
   */
  name: string;

  /**
   * Placeholder text for the select input.
   */
  placeholder?: string;

  /**
   * Options to be displayed in the select dropdown.
   * Can be an array of strings or any other type.
   */
  options: string[] | any[];

  /**
   * Help text to be displayed below the select input.
   */
  helpText?: string;

  /**
   * Callback function to handle changes in the select input.
   *
   * If a value is returned from this function, it will be used
   * as the new value for the select input instead of the value
   * returned by the default onChange handler.
   *
   * @param event - The change event.
   * @param value - The new value selected.
   * @param reason - The reason for the change.
   * @param details - Additional details about the change.
   */
  onChange?: BeamSelectOnChange;
}

export const BeamSelect = ({
  name,
  options,
  label,
  placeholder,
  optional,
  required,
  getOptionLabel,
  sx,
  helpText,
  disabled,
  onChange,
  isOptionEqualToValue,
}: BeamSelectProps & Omit<BeamInputLabelProps, 'htmlFor'> & Partial<AutocompleteProps<any, any, any, any>>) => {
  const formikContext = useFormikContext();
  const [field, meta, helper] = useField<string>(name);
  const { value } = field;

  return (
    <Box display={'flex'} flexDirection={'column'} gap={'8px'} sx={sx}>
      <BeamInputLabel label={label} optional={optional} required={required} htmlFor={name} />
      <BeamAutocomplete
        options={options}
        id={name}
        size={'small'}
        value={value !== undefined && value !== '' ? value : null}
        onChange={(e, value, reason, details) => {
          // Run the user provided onChange function if it exists
          let newValue = value;
          if (onChange !== undefined) {
            const transformedValue = onChange(e, value, reason, details);
            if (transformedValue !== undefined) {
              // If the onChange function returns a value, use that as the new value
              newValue = transformedValue;
            }
          }

          // Set the formik field value
          formikContext?.setFieldValue(name, newValue);
          helper.setTouched(true, false);
        }}
        disabled={formikContext.isSubmitting || disabled}
        autoComplete
        getOptionLabel={getOptionLabel}
        isOptionEqualToValue={isOptionEqualToValue}
        renderInput={params => (
          <TextField
            {...params}
            error={meta.touched && meta.error !== undefined}
            helperText={meta.touched && meta.error}
            onBlur={() => helper.setTouched(true, true)}
            required={required}
            placeholder={placeholder}
          />
        )}
      />

      <BeamHelpText helpText={helpText} />
    </Box>
  );
};
